"use client";

import { Link } from "@i18n/routing";
import { usePathname } from "@i18n/routing";
import { useUser } from "@saas/auth/hooks/use-user";
import { ColorModeToggle } from "@shared/components/ColorModeToggle";
import { Logo } from "@shared/components/Logo";
import { Button } from "@ui/components/button";
import { Sheet, SheetContent, SheetTrigger } from "@ui/components/sheet";
import { cn } from "@ui/lib";
import {
  Car,
  ContactIcon,
  HomeIcon,
  MenuIcon,
  StoreIcon,
  UserCircle,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { useSearchParams } from "next/navigation";
import { useDeviceType } from "../../../../app/hooks/ui";
import { DeviceTypeEnum } from "../../../../app/hooks/ui/useDevice/props";

export function NavBar() {
  const t = useTranslations();
  const { user } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const [isTop, setIsTop] = useState(true);
  const { deviceType } = useDeviceType();
  const searchParams = useSearchParams();

  const debouncedScrollHandler = useDebounceCallback(
    () => {
      setIsTop(window.scrollY <= 10);
    },
    150,
    {
      maxWait: 150,
    }
  );

  useEffect(() => {
    window.addEventListener("scroll", debouncedScrollHandler);
    debouncedScrollHandler();
    return () => {
      window.removeEventListener("scroll", debouncedScrollHandler);
    };
  }, [debouncedScrollHandler]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const menuItems: {
    label: string;
    href: string;
    icon: React.ReactNode;
  }[] = [
    {
      // label: t("common.menu.pricing"),
      label: "Início",
      href: "/",
      icon: <HomeIcon />,
    },
    {
      // label: t("common.menu.pricing"),
      label: "Veículos",
      href: "/listing",
      icon: <Car />,
    },
    {
      // label: t("common.menu.pricing"),
      label: "Contato",
      href: "/contato",
      icon: <ContactIcon />,
    },
    // {
    //   // label: t("common.menu.pricing"),
    //   label: "Lojas",
    //   href: "/lojas",
    //   icon: <StoreIcon />,
    // },
  ];

  const cupomDescontoEncoded = searchParams?.get("cd");
  const hasCoupon =
    cupomDescontoEncoded && typeof cupomDescontoEncoded === "string";

  const promoEncoded = searchParams?.get("p");
  const hasPromo = promoEncoded && typeof promoEncoded === "string";

  const referralEncoded = searchParams?.get("rf");
  const hasReferral = referralEncoded && typeof referralEncoded === "string";

  const limitedEncoded = searchParams?.get("lt");
  const hasLimited = limitedEncoded && typeof limitedEncoded === "string";

  const hasAnySpecialParam = hasCoupon || hasPromo || hasReferral || hasLimited;

  const isMenuItemActive = (href: string) => pathname === href;

  if (pathname === "/" || pathname === "/motos") return null;

  if (deviceType === DeviceTypeEnum.MOBILE) {
    return (
      <nav
        className={cn(
          "fixed bottom-0 left-0 z-50 w-full bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-gray-700",
          hasAnySpecialParam && "mt-[72px]"
        )}
      >
        <div className="flex justify-between px-4 py-2">
          {menuItems.map((menuItem) => (
            <Link
              key={menuItem.href}
              href={menuItem.href}
              className={cn(
                "flex flex-col items-center justify-center flex-1 py-2",
                isMenuItemActive(menuItem.href)
                  ? "text-blue-500"
                  : "text-gray-500"
              )}
            >
              {menuItem.icon}
              <span className="text-xs">{menuItem.label}</span>
            </Link>
          ))}
          <Link
            key={user ? "dashboard" : "login"}
            href={user ? "/app" : "/auth/login"}
            className="flex flex-col items-center justify-center flex-1 py-2"
            prefetch={!user}
          >
            <UserCircle className="w-6 h-6 text-gray-500" />
            <span className="text-xs text-gray-500">Perfil</span>
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={cn(
        `fixed top-0 left-0 z-40 w-full bg-white dark:bg-slate-700 ${
          isTop ? "shadow-none" : "bg-card/80 shadow-sm backdrop-blur-lg"
        } transition-shadow duration-200`,
        hasAnySpecialParam && isTop && "mt-[72px]"
      )}
      data-test="navigation"
    >
      <div className="container">
        <div
          className={`flex items-center justify-stretch gap-6 ${
            isTop ? "py-5" : "py-4"
          } transition-[padding] duration-200`}
        >
          <div className="flex flex-1 justify-start">
            <Link
              href="/"
              className="block hover:no-underline active:no-underline"
            >
              <Logo />
            </Link>
          </div>

          <div className="hidden grow justify-end md:flex">
            {menuItems.map((menuItem) => (
              <Link
                key={menuItem.href}
                href={menuItem.href}
                className={cn(
                  "block px-4 py-2 font-normal whitespace-nowrap leading-8 text-[15px]",
                  isMenuItemActive(menuItem.href)
                    ? "font-bold text-foreground"
                    : ""
                )}
              >
                {menuItem.label}
              </Link>
            ))}
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            <ColorModeToggle />
            {/* <LocaleSwitch /> */}

            <Sheet
              open={mobileMenuOpen}
              onOpenChange={(open) => setMobileMenuOpen(open)}
            >
              <SheetTrigger asChild>
                <Button
                  className="md:hidden"
                  size="icon"
                  variant="outline"
                  aria-label="Menu"
                >
                  <MenuIcon className="size-4" />
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[250px]" side="right">
                <div className="flex flex-col items-start justify-center">
                  {menuItems.map((menuItem) => (
                    <Link
                      key={menuItem.href}
                      href={menuItem.href}
                      className={cn(
                        "block px-3 py-2 font-medium text-base text-foreground/80",
                        isMenuItemActive(menuItem.href)
                          ? "font-bold text-foreground"
                          : ""
                      )}
                    >
                      {menuItem.label}
                    </Link>
                  ))}

                  <Link
                    key={user ? "dashboard" : "login"}
                    href={user ? "/app" : "/auth/login"}
                    className="block px-3 py-2 text-base"
                    prefetch={!user}
                  >
                    {user ? t("common.menu.dashboard") : t("common.menu.login")}
                  </Link>
                </div>
              </SheetContent>
            </Sheet>

            {user ? (
              <>
                <Button
                  key="dashboard"
                  className="border-2  hidden md:flex"
                  asChild
                  variant="outline"
                  size="lg"
                >
                  <Link
                    href="/app"
                    className="font-normal text-2xl"
                    style={{ fontWeight: 400 }}
                  >
                    {t("common.menu.dashboard")}
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <Button
                  key="signup"
                  className="border-2 hidden md:flex"
                  asChild
                  variant="outline"
                  size="lg"
                >
                  <Link
                    href="/planos"
                    className="px-8 py- font-normal text-2xl"
                  >
                    Cadastro
                  </Link>
                </Button>
                <Button
                  key="login"
                  className="hidden md:flex bg-primary-600 hover:bg-primary-600/90"
                  asChild
                  variant="default"
                  size="lg"
                >
                  <Link href="/app" className="px-8 py-6 font-normal text-2xl">
                    Entrar
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
